// The `openings` interface: the opening book, as v1 had it. An opening is a
// position, not a sequence of moves, so every question is asked of a
// position and a game reached by a different move order gets the same
// answer. The book covering a game is loaded by the game's first move, so
// the data arrives in two files and a game fetches one of them.
import { type Fen, type San } from '@chess/api-model';

export const OPENINGS_API = 'openings';
export const OPENINGS_VERSION = '1.0.0';

/** One named opening: the ECO code and the name, "Sicilian Defense: Najdorf Variation". */
export interface Opening {
  eco: string;
  name: string;
}

/** What a game was playing and how far it followed the theory. */
export interface BookReading {
  /** The deepest named position the game passed through. */
  opening: Opening;
  /** Index into the game's positions of that named position: the plies played to reach it. */
  ply: number;
  /** Index of the last position the book knows at all, named or not; the move played from it is the move out of the book. */
  lastKnownPly: number;
}

/** A move the book knows from a position, and the opening it heads towards. */
export interface OpeningContinuation {
  san: San;
  opening: Opening;
  /** Plies from the position after `san` to the opening it heads towards; 0 when that position is the opening. */
  distance: number;
}

/** One line of the book: the moves from the start to a named position, and the positions they pass through. */
export interface BookLine {
  /** The opening the line ends at. */
  opening: Opening;
  moves: San[];
  /** `positions[0]` is the start; `positions[i + 1]` is the position after `moves[i]`. */
  positions: Fen[];
}

export interface OpeningBook {
  /** The opening this exact position is, if it is one. */
  lookup(fen: Fen): Opening | undefined;
  /** Whether the book has anything to say here: a name, or a move to play. This is what "still in the book" means. */
  knows(fen: Fen): boolean;
  /** The moves the book knows from here, alphabetical by move. Empty means off the end of every line. */
  continuations(fen: Fen): OpeningContinuation[];
  /**
   * Every line of the book that ends at a position with this name, in the
   * data's order: a name given to several positions (Lichess names
   * "Sicilian Defense: Closed" eight times) has a line to each. Empty for a
   * name the book does not have.
   */
  lines(name: string): BookLine[];
  /** What a game was playing; `positions[0]` is the start. Undefined when the book names none of them. */
  book(positions: Fen[]): BookReading | undefined;
  /** How many openings the book holds. */
  readonly size: number;
}

export interface Openings {
  /** The book covering games that open with this move. Loaded once, then cached for the session. */
  load(firstMove: San): Promise<OpeningBook>;
}

export { buildBook, epdOf, type BookData, type BookNode, type BookRules } from './book.ts';
